import React, { useState, useEffect } from 'react';
import { formatTranslation } from 'intl';
import { useIntl } from 'react-intl';
import { useGalleryContext } from '..';
import { identitySelector } from '../selectors';
import { DocumentationBaseIds } from 'shared/types/documentation';
import { useIssueOverlayControls } from '../../../documentation/DocumentationDataGrid/components/IssueOverlayControls';
import { LocationOffOutlined, LocationOn } from '@mui/icons-material';
import { useMountedRef } from 'hooks/useMountRef';
import { ActionButton } from './actionButton';
import { getCanBeUsedAsMap } from 'components/documentation/model';
import { getPendingConversionPdfTitle } from './model';
import { fileKindService } from 'services/fileService';
import { DocumentationTranslationKeys } from 'intl/keys';

interface ShowIssuesButtonProps {
  isMobile: boolean;
}

export const ShowIssuesButton: React.FC<ShowIssuesButtonProps> = ({
  isMobile,
}) => {
  const intl = useIntl();
  const mountedRef = useMountedRef();
  const [file] =
    useGalleryContext<DocumentationBaseIds>().useMainDisplayedFile(
      identitySelector
    );
  const canBeUsedAsMap = getCanBeUsedAsMap(
    file?.data?.extension,
    file?.imageConvertStatus
  );
  const { issuesVisibleStore, setFile, toggleVisible, disabled } =
    useIssueOverlayControls();

  const [isActive, setIsActive] = useState(issuesVisibleStore.get());

  // Tells the issue overlay (a parent provider) which gallery file is shown.
  useEffect(() => {
    setFile(file);
  }, [file, setFile]);

  useEffect(() => {
    return issuesVisibleStore.subscribe(() => {
      if (!mountedRef.current) return;
      setIsActive(issuesVisibleStore.get());
    });
  }, [issuesVisibleStore, mountedRef]);
  const showIssuesActionLabel = formatTranslation(intl, DocumentationTranslationKeys.showIssuesAction,);
  const hideIssuesActionLabel = formatTranslation(intl, DocumentationTranslationKeys.hideIssuesAction,);
  const label = isActive ? hideIssuesActionLabel : showIssuesActionLabel;
  let locationTooltipTitle = label;
  if (file && fileKindService.isPendingConversionPdf(file)) {
    locationTooltipTitle = getPendingConversionPdfTitle(intl);
  } else if (!canBeUsedAsMap) {
    locationTooltipTitle = formatTranslation(intl, DocumentationTranslationKeys.fileNotSupportedAsMap,);
  } else if (disabled) {
    locationTooltipTitle = formatTranslation(intl, DocumentationTranslationKeys.fileNotUsedAsMap,);
  }

  return (
    <ActionButton
      isMobile={isMobile}
      onClick={toggleVisible}
      disabled={disabled}
      tooltipTitle={locationTooltipTitle}
      label={label}
      icon={isActive ? <LocationOn /> : <LocationOffOutlined />}
    />
  );
};
